import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { ProductItem } from '../../services/product.service';
import { useCart } from '../../context/CartContext';
import { showSuccess } from '../../utils/notificationUtils';
import {
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';

interface ProductCardProps {
  product: ProductItem;
  customUrl?: string;
  hideAddToCart?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  customUrl,
  hideAddToCart,
}) => {
  const { id, name, unit, original_price, current_price, status, images } =
    product;

  const { t } = useTranslation();
  const isOutOfStock = status !== 'active'; // Check if product is not active
  const image = getProductImageUrl(images?.[0]);
  const slug = id;

  // Use customUrl if provided, otherwise use the default product URL
  const productUrl = customUrl || `/products/${slug}`;

  const cart = useCart();

  const handleAddToCart = () => {
    if (isOutOfStock) return;

    // Cast product to any to access all properties
    const productWithVariants = product as any;
    
    // Debug: Log product info
    console.log('ProductCard - Product:', {
      id: product.id,
      name: product.name,
      type: productWithVariants.type,
      variants: productWithVariants.variants,
      hasVariants: productWithVariants.type === 'multiple_variants' && Array.isArray(productWithVariants.variants) && productWithVariants.variants.length > 0
    });

    // Check if product has variants
    const hasVariants = productWithVariants.type === 'multiple_variants' && 
      Array.isArray(productWithVariants.variants) && 
      productWithVariants.variants.length > 0;

    // Get first variant if product has variants
    const firstVariant = hasVariants && productWithVariants.variants ? productWithVariants.variants[0] : null;

    // Debug: Log variant info
    console.log('ProductCard - Variant check:', {
      hasVariants,
      firstVariant,
      productType: productWithVariants.type,
      variantsArray: productWithVariants.variants
    });

    // Determine product type
    let productType = 'single'; // Default type
    if (product.type === 'multiple_variants') {
      productType = 'multiple_variants';
    } else if (product.type === 'fruit_box') {
      productType = 'fruit_box';
    }

    // Create cart item with variant info if available
    const cartItem = {
      id: hasVariants && firstVariant ? `${id}_${firstVariant._id}` : id,
      productId: id,
      name: hasVariants && firstVariant ? `${name} - ${firstVariant.name}` : name,
      price: hasVariants && firstVariant 
        ? firstVariant.pricing.original_price
        : (current_price || original_price || 0),
      originalPrice: hasVariants && firstVariant 
        ? firstVariant.pricing.original_price 
        : (original_price || 0),
      salePrice: product.sale_price,
      currentPrice: hasVariants && firstVariant 
        ? firstVariant.pricing.original_price
        : (current_price || original_price || 0),
      image: getProductImageUrl(images?.[0]),
      images: images || [],
      quantity: 1,
      weight: unit || '',
      unit: unit || '',
      stock: 10,
      type: productType,
      selected: false,
      variantId: hasVariants && firstVariant ? firstVariant._id : undefined,
      variantName: hasVariants && firstVariant ? firstVariant.name : undefined,
    };

    cart.addItem(cartItem, 1);

    // Show success notification
    showSuccess(
      t('product.addedToCart'),
      t('product.addedToCartMessage', { name: cartItem.name })
    );
  };

  // const discount = originalPrice
  //   ? Math.round(((originalPrice - price) / originalPrice) * 100)
  //   : 0;

  return (
    <div
      className={`group relative bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100`}
    >
      {/* Out of stock badge */}
      {isOutOfStock && (
        <div className='absolute top-0 right-0 z-10 m-2'>
          <div className='bg-red-500 text-white px-2 py-1 rounded-md text-xs font-medium'>
            {status === 'out_of_stock'
              ? t('product.outOfStock')
              : 'Hết hàng'}
          </div>
        </div>
      )}

      {/* Product image with background */}
      <div className='bg-gray-50 p-1 sm:p-2 rounded-t-lg'>
        <Link
          to={productUrl}
          className='block overflow-hidden'
          style={{ aspectRatio: '1/1' }}
        >
          <img
            src={image}
            alt={name}
            className='w-full h-full object-contain group-hover:scale-105 transition-transform duration-300'
            loading='lazy'
            onError={handleProductImageError}
          />
        </Link>
      </div>

      {/* Product details */}
      <div className='p-1.5 sm:p-3 md:p-4'>
        {/* <div className='mb-0.5 text-[8px] sm:text-xs text-primary/80 font-medium'>
          {category}
        </div> */}
        <Link to={productUrl} className='block'>
          <h3
            className={`text-[9px] xs:text-xs sm:text-sm md:text-base font-medium ${
              isOutOfStock ? 'text-gray-500' : 'text-secondary'
            } line-clamp-2 min-h-[2.5rem] xs:min-h-[2rem] mb-1 hover:text-primary transition-colors`}
          >
            {name}
          </h3>
        </Link>

        <div className='flex items-end justify-between mt-1 sm:mt-2'>
          <div>
            <div
              className={`font-semibold ${
                isOutOfStock ? 'text-gray-500' : 'text-primary'
              } text-[10px] sm:text-sm md:text-base`}
            >
              {current_price?.toLocaleString('vi-VN')}₫
            </div>
            {original_price && original_price !== current_price && (
              <div className='text-gray-500 text-[8px] sm:text-xs line-through'>
                {original_price.toLocaleString('vi-VN')}₫
              </div>
            )}
          </div>

          {!isOutOfStock && !hideAddToCart && (
            <button
              className='bg-primary hover:bg-primary-dark text-white p-1 sm:p-2 md:p-3 rounded-full transition duration-200 shadow-sm'
              aria-label={t('product.addToCart')}
              onClick={handleAddToCart}
              title={t('product.addToCart')}
            >
              <ShoppingCartIcon className='h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6' />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
